/**
 * NotificationBell — sino agregado de pendencias (Bloco UI / UI.2).
 *
 * Visual inspirado nas 4 referencias enviadas pela usuaria:
 * - Botao sino com badge pill accent (cobre, NAO pink)
 * - Click abre dropdown via Portal com lista clicavel
 * - Cada fonte mostra label + count em pill + leva pra tela
 *
 * Reusa:
 * - useNotificacoesAgregadas (hook novo) — agregacao por permissao
 * - useWhatsappNaoLidasGlobal (ja existe) — embutido no hook
 * - Pattern de Portal + posicionamento do RailFlyout (Sidebar)
 *
 * Comportamento:
 * - Click fora ou Escape fecha
 * - Click num item: navega + fecha
 * - Reposiciona em scroll/resize da janela (closeOnly)
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FiBell } from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import useNotificacoesAgregadas from "../../hooks/useNotificacoesAgregadas";
import {
  BellBadge,
  BellButton,
  BellWrapper,
  Dropdown,
  DropdownEmpty,
  DropdownHeader,
  DropdownItem,
} from "./styles";


export default function NotificationBell() {
  const navigate = useNavigate();
  const { total, fontes } = useNotificacoesAgregadas();

  const [aberto, setAberto] = useState(false);
  const [pos, setPos] = useState({ top: 0, right: 0 });
  const btnRef = useRef(null);

  const fontesPermitidas = fontes.filter((f) => f.permitido);

  const recalcularPos = useCallback(() => {
    const btn = btnRef.current;
    if (!btn) return;
    const rect = btn.getBoundingClientRect();
    setPos({
      top: rect.bottom + 8,
      right: window.innerWidth - rect.right,
    });
  }, []);

  const abrir = useCallback(() => {
    recalcularPos();
    setAberto(true);
  }, [recalcularPos]);

  // Fechar em click fora / Escape / scroll / resize
  useEffect(() => {
    if (!aberto) return undefined;
    function onClickFora(e) {
      const btn = btnRef.current;
      if (btn && btn.contains(e.target)) return;
      // dropdown vive em portal — verifica via data-attribute
      if (e.target.closest && e.target.closest("[data-notification-dropdown]")) {
        return;
      }
      setAberto(false);
    }
    function onKey(e) {
      if (e.key === "Escape") setAberto(false);
    }
    function onScroll() {
      setAberto(false);
    }
    document.addEventListener("mousedown", onClickFora);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      document.removeEventListener("mousedown", onClickFora);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [aberto]);

  function handleItemClick(url) {
    setAberto(false);
    navigate(url);
  }

  return (
    <BellWrapper>
      <BellButton
        ref={btnRef}
        type="button"
        onClick={() => (aberto ? setAberto(false) : abrir())}
        aria-label={
          total > 0
            ? `${total} notificacao(s) pendente(s)`
            : "Nenhuma notificação pendente"
        }
        aria-expanded={aberto}
        title={total > 0 ? `${total} pendencia(s)` : "Notificações"}
      >
        <FiBell size={16} />
        {total > 0 && (
          <BellBadge>
            {total > 99 ? "99+" : total}
          </BellBadge>
        )}
      </BellButton>

      {aberto &&
        createPortal(
          <Dropdown
            data-notification-dropdown
            style={{ top: `${pos.top}px`, right: `${pos.right}px` }}
            role="menu"
          >
            <DropdownHeader>
              <h4>Pendencias</h4>
              {total > 0 ? (
                <small>{total} no total</small>
              ) : (
                <small>tudo em dia</small>
              )}
            </DropdownHeader>

            {fontesPermitidas.length === 0 ? (
              <DropdownEmpty>
                Você não tem nenhum módulo com pendências acionáveis.
              </DropdownEmpty>
            ) : (
              fontesPermitidas.map((f) => (
                <DropdownItem
                  key={f.id}
                  type="button"
                  role="menuitem"
                  onClick={() => handleItemClick(f.url)}
                  data-empty={f.count === 0}
                  title={`Abrir ${f.label}`}
                >
                  <span className="label">{f.label}</span>
                  <span className="count">{f.count}</span>
                </DropdownItem>
              ))
            )}
          </Dropdown>,
          document.body,
        )}
    </BellWrapper>
  );
}
